/**
 * ModelHistoryPanel — per-slot change-log drawer (GET /models/history?slot=).
 */

import React from 'react';
import { Box, Drawer, Typography, IconButton, Skeleton, Alert, Button, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { formatDistanceToNow } from 'date-fns';
import EmptyState from '../../common/EmptyState';
import { colors, typography } from '../../../theme/designTokens';
import { ModelChangeLogRow, ModelSlot } from '../../../services/quillAdminService';
import { SLOT_LABELS } from './slots';

export interface ModelHistoryPanelProps {
  open: boolean;
  slot: ModelSlot | null;
  rows: ModelChangeLogRow[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onClose: () => void;
}

export const ModelHistoryPanel: React.FC<ModelHistoryPanelProps> = ({
  open,
  slot,
  rows,
  loading,
  error,
  onRetry,
  onClose,
}) => {
  const sorted = [...rows].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: '90vw', sm: 420 }, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontFamily: typography.fonts.heading, fontWeight: 600, fontSize: typography.sizes.lg }}>
            History — {slot ? SLOT_LABELS[slot] : ''}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {loading && (
          <Box>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1 }} />
            ))}
          </Box>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        {!loading && !error && sorted.length === 0 && (
          <EmptyState icon="no_data" title="No history yet" description="No changes have been recorded for this slot." />
        )}

        {!loading &&
          !error &&
          sorted.map((row, idx) => (
            <Box key={`${row.slot}-${row.created_at}-${idx}`}>
              <Box sx={{ py: 1.5 }}>
                <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: typography.sizes.sm }}>
                  {row.old_model ?? '(none)'} <Box component="span" sx={{ color: colors.deepTeal[500] }}>→</Box> {row.new_model}
                </Typography>
                {row.reason && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    {row.reason}
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}>
                  {row.actor_email} • {formatDistanceToNow(new Date(row.created_at), { addSuffix: true })}
                </Typography>
              </Box>
              {idx < sorted.length - 1 && <Divider />}
            </Box>
          ))}
      </Box>
    </Drawer>
  );
};

export default ModelHistoryPanel;
